/*
 * ********************************************************************
 *  GlobePayroll Confkeyential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbkeyden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ElementRef, Injectable, Renderer2, TemplateRef, ViewContainerRef} from '@angular/core';

import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {map} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {Report} from './report';
import {
  CliRegDTO,
  DateOptions,
  FieldZone,
  MassActionFilterSaveResponse,
  OutputReport,
  ReportEntity,
  ReportingFields,
  ReportTimeDateOption
} from './reports.interfaces';

import Handsontable from 'handsontable';
import {ComponentLoaderFactory} from 'ngx-bootstrap/component-loader';
import {PopoverConfig, PopoverDirective} from 'ngx-bootstrap/popover';
import {PositioningService} from 'ngx-bootstrap/positioning';
import {DATE_FORMAT} from '../app.constants';
import {AnyData, ApiService} from '../shared/api';
import {LocalizationService} from '../shared/localization/localization.service';
import {PopoverService} from '../shared/ui/popover.service';

export const joinWithLineBreak = (value: any) => {
  if (Array.isArray(value)) {
    return value
      .map(v => `<div class="multi-value-item">${v != null ? v : '&nbsp;'}</div>`)
      .join('');
  }
  return value;
};

@Injectable()
export class ReportsService {
  static apiStatic: ApiService;

  constructor(apiStatic: ApiService,
              private api: ApiService) {
    ReportsService.apiStatic = apiStatic;
  }

  public isChanged: boolean = false;
  public $reportUpdated: Subject<Report> = new Subject();
  public $reportTimeTypeChanged: Subject<string> = new Subject();

  public fieldsSubject: BehaviorSubject<ReportingFields> = new BehaviorSubject<ReportingFields>(null);
  public currentFields = this.fieldsSubject.asObservable();
  public isFieldsLoaded: boolean = false;
  public zonesMap: Map<string, FieldZone> = new Map<string, FieldZone>();

  private firstDayOfCurrentMonth = moment().startOf('month');
  private lastDayOfCurrentMonth = moment().endOf('month');

  private firstDayOfLastMonth = moment().subtract(1, 'months').startOf('month');
  private lastDayOfLastMonth = moment().subtract(1, 'months').endOf('month');

  private firstDayOfCurrentYear = moment().startOf('year');
  private lastDayOfCurrentYear = moment().endOf('year');

  private firstDayOfLastYear = moment().subtract(1, 'years').startOf('year');
  private lastDayOfLastYear = moment().subtract(1, 'years').endOf('year');

  public entityPathsToDisplay: string[] = [];

  public singleDateOptions: DateOptions[] = [
    {
      id: ReportTimeDateOption.TODAY,
      text: 'REPORTS.today',
      beginDate: moment().format(DATE_FORMAT),
      endDate: moment().format(DATE_FORMAT),
      isDefault: true
    },
    {
      id: ReportTimeDateOption.LAST_DAY_PREVIOUS_MONTH,
      text: 'REPORTS.last_day_of_previous_month',
      beginDate: this.lastDayOfLastMonth.format(DATE_FORMAT),
      endDate: this.lastDayOfLastMonth.format(DATE_FORMAT),
    },
    {
      id: ReportTimeDateOption.LAST_DAY_PREVIOUS_YEAR,
      text: 'REPORTS.last_day_of_previous_year',
      beginDate: this.lastDayOfLastYear.format(DATE_FORMAT),
      endDate: this.lastDayOfLastYear.format(DATE_FORMAT),
    },
    {
      id: ReportTimeDateOption.CUSTOM,
      text: 'REPORTS.custom_date',
      beginDate: '',
      endDate: '',
    }
  ];

  public betweenDateOptions: DateOptions[] = [
    {
      id: ReportTimeDateOption.CURRENT_MONTH,
      text: 'REPORTS.current_month',
      beginDate: this.firstDayOfCurrentMonth.format(DATE_FORMAT),
      endDate: this.lastDayOfCurrentMonth.format(DATE_FORMAT),
      isDefault: true
    },
    {
      id: ReportTimeDateOption.LAST_MONTH,
      text: 'REPORTS.last_month',
      beginDate: this.firstDayOfLastMonth.format(DATE_FORMAT),
      endDate: this.lastDayOfLastMonth.format(DATE_FORMAT),
    },
    {
      id: ReportTimeDateOption.CURRENT_YEAR,
      text: 'REPORTS.current_year',
      beginDate: this.firstDayOfCurrentYear.format(DATE_FORMAT),
      endDate: this.lastDayOfCurrentYear.format(DATE_FORMAT),
    },
    {
      id: ReportTimeDateOption.LAST_YEAR,
      text: 'REPORTS.last_year',
      beginDate: this.firstDayOfLastYear.format(DATE_FORMAT),
      endDate: this.lastDayOfLastYear.format(DATE_FORMAT),
    },
    {
      id: ReportTimeDateOption.CUSTOM,
      text: 'REPORTS.custom_dates',
      beginDate: '',
      endDate: '',
    }
  ];

  public static readOnlyRenderer(instance: any, td: any, row: any, col: any,
                                 prop: any, value: any, cellProperties: any): any {
    const isMultiValue = Array.isArray(value);
    value = joinWithLineBreak(value);
    if (value == null) { value = ''; }
    Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
    td.innerHTML = value;
    td.style.color = '#000';
    td.style.background = '#ffffff';
    ReportsService.applyMultiValueStyle(td, isMultiValue);
    return td;
  }

  public static readOnlyNumberRenderer(localization: LocalizationService, instance: any, td: any, row: any, col: any,
                                       prop: any, value: any, cellProperties: any): any {
    const isMultiValue = Array.isArray(value);
    value = ReportsService.formatNumberRendering(value, localization);
    if (value == null) { value = ''; }
    Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
    td.innerHTML = value;
    td.style.color = '#000';
    td.style.background = '#ffffff';
    ReportsService.applyMultiValueStyle(td, isMultiValue);
    return td;
  }

  private static applyMultiValueStyle(td: HTMLElement, isMultiValue: boolean): void {
    if (isMultiValue) {
      td.classList.add('multi-value-cell');
    } else {
      td.classList.remove('multi-value-cell');
    }
  }

  public static isValidFileValue(value: AnyData): boolean {
    if (value == null) {
      return false;
    }
    const stringValue = String(value).trim();
    if (!stringValue) {
      return false;
    }
    const rawId = stringValue.includes(':') ? stringValue.split(':')[1] : stringValue;
    return rawId.trim() !== '' && Number.isFinite(Number(rawId));
  }

  public static formatNumberRendering(value: any, localizationService: LocalizationService) {
    if (value === null || value === undefined) {
      return '';
    } else if (Array.isArray(value)) {
      return value.map((val: any) =>
        `<div class="multi-value-item">${val != null ? localizationService.localizeNumber(Number(val), 'DECIMAL_ALL_DIGITS') : '&nbsp;'}</div>`).join('');
    } else {
      return localizationService.localizeNumber(Number(value), 'DECIMAL_ALL_DIGITS');
    }
  }

  public static fileDetailButtonRenderer(renderer: Renderer2,
                                         viewContainerRef: ViewContainerRef,
                                         componentLoaderFactory: ComponentLoaderFactory,
                                         positioningService: PositioningService,
                                         fileDetailPopover: TemplateRef<unknown>,
                                         popoverService: PopoverService,
                                         popoverTitle: string,
                                         ...tableArgs: any[]) {
    popoverService.setRenderer(renderer);

    const [, td, , , , value, ] = tableArgs;

    Handsontable.dom.empty(td);

    td.style.textAlign = 'center';
    td.style.verticalAlign = 'middle';

    const isMultiValue = Array.isArray(value);
    ReportsService.applyMultiValueStyle(td, isMultiValue);

    if (isMultiValue) {
      value.forEach((singleValue: AnyData) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'multi-value-item';

        if (ReportsService.isValidFileValue(singleValue)) {
          wrapper.appendChild(ReportsService.createFileDetailButton(singleValue, renderer, viewContainerRef,
            componentLoaderFactory, positioningService, fileDetailPopover, popoverService, popoverTitle));
        } else {
          wrapper.innerHTML = '&nbsp;';
        }

        td.appendChild(wrapper);
      });
    } else if (ReportsService.isValidFileValue(value)) {
      td.appendChild(ReportsService.createFileDetailButton(value, renderer, viewContainerRef,
        componentLoaderFactory, positioningService, fileDetailPopover, popoverService, popoverTitle));
    }

    return td;
  }

  private static createFileDetailButton(value: string,
                                        renderer: Renderer2,
                                        viewContainerRef: ViewContainerRef,
                                        componentLoaderFactory: ComponentLoaderFactory,
                                        positioningService: PositioningService,
                                        fileDetailPopover: TemplateRef<unknown>,
                                        popoverService: PopoverService,
                                        popoverTitle: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'btn btn-xs btn-icon white';

    const paperclipIcon = document.createElement('i');
    paperclipIcon.className = 'fa fa-light fa-file-circle-info';
    button.appendChild(paperclipIcon);

    const spinnerIcon = document.createElement('i');
    spinnerIcon.className = 'fa fa-spinner fa-spin';
    spinnerIcon.style.display = 'none';
    button.appendChild(spinnerIcon);

    const elementRef = new ElementRef(button);

    const popoverDirective = new PopoverDirective(
      new PopoverConfig(),
      elementRef,
      renderer,
      viewContainerRef,
      componentLoaderFactory,
      positioningService
    );

    popoverDirective.popover = fileDetailPopover;
    popoverDirective.popoverTitle = popoverTitle;
    popoverDirective.triggers = 'manual'; // Manual trigger
    popoverDirective.container = 'body';
    popoverDirective.placement = 'left';

    popoverDirective.ngOnInit(); // Initialize the directive

    button.addEventListener('click', () => {
      popoverService.hideAllPopovers();
      paperclipIcon.style.display = 'none'; // Hide paperclip icon
      spinnerIcon.style.display = 'block'; // Show spinner icon

      const fileId: number = value.includes(':') ? +value.split(':')[1] : +value;

      ReportsService.apiStatic.get(`/file/api/v1/file-description?${`search=id==${fileId}`}`)
        .subscribe(data => {
          popoverService.setData(data);
          spinnerIcon.style.display = 'none'; // Hide spinner icon
          paperclipIcon.style.display = 'block'; // Show paperclip icon
          popoverService.hideAllPopovers(); // Hide all other popovers
          popoverDirective.show(); // Show popover after data is fetched
          popoverService.registerPopover(popoverDirective, elementRef); // Register the popover
        });
    });

    return button;
  }

  public markAsChanged() {
    this.isChanged = true;
  }

  public markAsPristine() {
    this.isChanged = false;
  }

  public getReportsList(): Observable<ReportEntity[]> {
    return this.api.get(`/elasticquery/api/v1/reportTemplate/getTemplates`)
      .pipe(
        map((response: any[]) => response.map(this.mapLegacyTemplates())),
      );
  }

  public getMassActionsTemplates(): Observable<ReportEntity[]> {
    return this.api.get(`/elasticquery/api/v1/mass-action-filters`)
      .pipe(
        map((response: any[]) => response.map(this.mapLegacyTemplates()))
      );
  }

  public getReport(reportId: number, isReportStandard = false): Observable<ReportEntity> {
    let url = `/elasticquery/api/v1/reportTemplate/getTemplates/${reportId}`;
    if (isReportStandard) {
      url = url + `?isStandard=${isReportStandard}`;
    }
    return this.api.get(url)
      .pipe(map(this.mapLegacyTemplates()));
  }

  public getMassActionFilter(filterId: number): Observable<ReportEntity> {
    return this.api.get(`/elasticquery/api/v1/mass-action-filters/${filterId}`)
      .pipe(map(this.mapLegacyTemplates()));
  }

  private mapLegacyTemplates() {
    return (element) => {
      element.outputFields = element.outputFields ? Object.values(element.outputFields) : [];
      element.filterFields = element.filterFields ? Object.values(element.filterFields) : [];
      this.handleLegacyTemplate(element);
      return element;
    };
  }

  private handleLegacyTemplate(element: any) {
    for (const field of element.outputFields) {
      this.handleLegacyTemplateField(field);
    }
    for (const field of element.filterFields) {
      this.handleLegacyTemplateField(field);
    }
  }

  public getFilterReportForMassUpdate(reportId: number,
                                      fields: {transIds: string[], name: string, fieldTypes?: string[]}[] = [])
    : Observable<ReportEntity> {
    const filterReport$ = this.getMassActionFilter(reportId);
    return filterReport$.pipe(
      map(value => {
        value.outputFields = fields.map(field => ({
          transIds: field.transIds,
          field: field.name,
          filterDocument: 'contract',
          fieldTypes: field.fieldTypes ? field.fieldTypes : ['LABEL'],
          fieldGroupType: 'UNIQUE_MANDATORY',
          fieldSortedBy: '',
          fieldSortPosition: 1,
          fieldPosition: 1,
          isRetro: false,
          metadatas: {disabled: false},
          displayRetro: false,
          displayEntityPath: false
        }));
        return value;
      }));
  }

  private handleLegacyTemplateField(field) {
    if (!field.transIds) {
      field.transIds = [field.title];
    }
    if (field.path.constructor === Array && field.path[0].constructor === String) {
      const newPath = [];
      for (let i = 0; i < field.path.length; i++) {
        let pathValue = field.path[i];
        switch (pathValue) {
          case 'empContract':
            pathValue = 'REPORTS.contract_fields';
            break;
          case 'entity':
            pathValue = 'REPORTS.entity_fields';
            break;
          case 'employee':
            pathValue = 'REPORTS.employee_fields';
            break;
          case 'payroll':
            pathValue = 'REPORTS.payroll_fields';
            break;
        }
        newPath.push([pathValue]);
      }
      field.path = newPath;
    }
  }

  public getReportingFields(): Observable<any> {
    return this.api.get(`/repository/api/v1/regulation/reporting-fields`);
  }

  public getRawReportingFields(): Observable<any> {
    return this.api.get(`/repository/api/v1/regulation/raw-reporting-fields`);
  }

  public getReportingFieldsByCliRegId(cliRegId: number): Observable<ReportingFields> {
    return this.api.config.get(`/regulation/${cliRegId}/reporting-fields`);
  }

  public createReport(report: OutputReport): Observable<any> {
    return this.api.post(`/elasticquery/api/v1/reportTemplate`, report);
  }

  public updateReport(report: OutputReport): Observable<Response> {
    const isStandard = report.isStandard;
    return this.api.post(isStandard ?
      `/elasticquery/api/v1/reportTemplate/${report.id}?isStandard=${isStandard}` :
      `/elasticquery/api/v1/reportTemplate/${report.id}`, report);
  }

  public exists(report: OutputReport): Observable<Response> {
    const reportTitle = encodeURIComponent(report.title);
    const isStandard = report.isStandard;

    return this.api.get(`/elasticquery/api/v1/reportTemplate/exists?title=${reportTitle}&isStandard=${isStandard}`);
  }

  public checkWriteAccessOnRootCliReg(): Observable<CliRegDTO> {
    return this.api.get('/repository/api/v1/client/cli-regs/root');
  }

  public deleteReport(id: number, isReportStandard = false): Observable<Response> {
    return this.api.delete(isReportStandard ?
      `/elasticquery/api/v1/reportTemplate/${id}?isStandard=${isReportStandard}` :
      `/elasticquery/api/v1/reportTemplate/${id}`);
  }

  public shareTemplate(templateId: number, ids: number[], editingAllowed: boolean, isPublic: boolean): Observable<any> {
    return this.api.post('/elasticquery/api/v1/reportTemplate/shareTemplate', null, {
      requestOptions: {
        params: {
          templateId,
          userIds: ids,
          editingAllowed,
          isPublic
        }
      }
    });
  }

  public createMassActionFilter(report: OutputReport): Observable<MassActionFilterSaveResponse> {
    return this.api.silent.post(`/elasticquery/api/v1/mass-action-filters`, report);
  }

  public duplicateMassActionFilter(sourceId: number, title: string): Observable<MassActionFilterSaveResponse> {
    return this.api.silent.post(`/elasticquery/api/v1/mass-action-filters?sourceId=${sourceId}`, {title});
  }

  public updateMassActionFilter(report: OutputReport): Observable<Response> {
    return this.api.put(`/elasticquery/api/v1/mass-action-filters/${report.id}`, report);
  }

  public renameMassActionFilter(filterId: number, title: string): Observable<Response> {
    return this.api.silent.patch(`/elasticquery/api/v1/mass-action-filters/${filterId}?title=${encodeURIComponent(title)}`, null);
  }

  public deleteMassActionFilter(filterId: number): Observable<Response> {
    return this.api.delete(`/elasticquery/api/v1/mass-action-filters/${filterId}`);
  }

  public isMassActionFilterTitleAvailable(title: string, excludeId?: number): Observable<boolean> {
    let url = `/elasticquery/api/v1/mass-action-filters/title-availability?title=${encodeURIComponent(title)}`;
    if (excludeId != null) {
      url = url + `&excludeId=${excludeId}`;
    }
    return this.api.get(url);
  }
}
